import React from "react";
import { useFormContext, Controller } from "react-hook-form";

import {
  TaskPromptProps,
  ValidationConstraints,
} from "@/components/tasks/types";
import { sanitizeName } from "@/utils/strings";
import { PROMPT_COMPONENTS } from "./elements";
import PromptHeader from "./elements/shared/PromptHeader";

// Component
export default function TaskPrompt({
  prompt,
  readOnly = false,
  showType = false,
  showValue = true,
  richTextDescription = true,
  fieldName,
}: TaskPromptProps) {
  const formContext = useFormContext();

  // get fieldName from prompt name if it's not provided
  if (!fieldName && prompt?.name) {
    fieldName = sanitizeName(`prompt_${prompt.name}`);
  }

  const { name, header, description, type, options } = prompt || {};

  // Extract validation constraints from options
  const getValidationConstraints = (): ValidationConstraints | undefined => {
    const typedOptions = options as any;
    return typedOptions?.validation || {};
  };

  const isRequired = getValidationConstraints()?.required || false;

  // Convert validation constraints to react-hook-form rules
  const getValidationRules = () => {
    const constraints = getValidationConstraints();
    if (!constraints) return {};

    const rules: any = {};

    if (constraints.required) {
      if (type === 'checkbox') {
        rules.required = {
          value: true,
          message: constraints.customMessage || `${name} must be checked`
        };
      } else {
        rules.required = {
          value: true,
          message: constraints.customMessage || `${name} is required`
        };
      }
    }

    if (constraints.minLength) {
      rules.minLength = {
        value: constraints.minLength,
        message: `${name} must be at least ${constraints.minLength} characters`
      };
    }

    if (constraints.maxLength) {
      rules.maxLength = {
        value: constraints.maxLength,
        message: `${name} must be no more than ${constraints.maxLength} characters`
      };
    }

    if (constraints.pattern) {
      rules.pattern = {
        value: new RegExp(constraints.pattern),
        message: constraints.customMessage || `${name} format is invalid`
      };
    }

    if (constraints.min !== undefined) {
      rules.min = {
        value: constraints.min,
        message: `${name} must be at least ${constraints.min}`
      };
    }

    if (constraints.max !== undefined) {
      rules.max = {
        value: constraints.max,
        message: `${name} must be no more than ${constraints.max}`
      };
    }

    return rules;
  };

  const renderPromptByType = () => {
    // if we're readOnly, show the value
    if (readOnly) {
      return (
        <div className="p-3 border border-gray-200 rounded bg-gray-50 min-h-[38px]">
          {prompt?.value || (
            <span className="text-gray-400">No response</span>
          )}
        </div>
      );
    }

    const Component = PROMPT_COMPONENTS[type];

    if (!Component) {
      return (
        <div className="text-red-500">Unsupported prompt type: {type}</div>
      );
    }

    return (
      <Controller
        name={fieldName || ""}
        control={formContext.control}
        rules={getValidationRules()}
        render={({ field, fieldState: { error } }) => (
          <Component
            name={name}
            options={options as any}
            field={field}
            error={error}
            readOnly={readOnly}
          />
        )}
      />
    );
  };

  // conversation breaks are a special case - they don't have a label/description
  // and just render as a horizontal rule
  if (type === "convo-break") {
    return <hr className="my-6 border-t border-gray-300" />;
  }

  return (
    <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
      <PromptHeader
        name={name}
        header={header}
        description={description}
        isRequired={isRequired}
        richTextDescription={richTextDescription}
      />

      {showValue && <div className="space-y-3">{renderPromptByType()}</div>}

      {/* Type indicator for development/debugging */}
      {showType && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
            Type: {type}
          </span>
        </div>
      )}
    </div>
  );
}
